import { ChartBar, Gear, ListChecks, Storefront, TShirt } from "@phosphor-icons/react"
import { useAuth0 } from "@auth0/auth0-react"
import { useMemo } from "react"

import type { HeaderTab } from "~/components/reusable/HeaderTabs"
import useShowInternalFeatures from "~/hooks/useShowInternalFeatures"
import { useUser } from "~/providers/userProvider"
import { canOpenProductReport } from "~/utils/allocationAccess"

const PRODUCT_REPORT_PATH = "/allocation/reports/products"

const MODULE_TABS: HeaderTab[] = [
  { label: "Replenishment", path: "/allocation/replenishment", icon: <ListChecks /> },
  { label: "Reports", path: "/allocation/reports", icon: <ChartBar /> },
  { label: "Settings", path: "/allocation/settings", icon: <Gear /> },
]
const REPORT_TABS: HeaderTab[] = [
  { label: "Stores", path: "/allocation/reports/stores", icon: <Storefront /> },
  { label: "Products", path: PRODUCT_REPORT_PATH, icon: <TShirt /> },
]
const PRODUCT_REPORT_MODULE_TABS: HeaderTab[] = [
  { label: "Reports", path: PRODUCT_REPORT_PATH, icon: <ChartBar /> },
]
const PRODUCT_REPORT_TABS = REPORT_TABS.filter((tab) => tab.path === PRODUCT_REPORT_PATH)

export type AllocationAccess = {
  level: "full" | "productReport" | "none"
  /** Where the sidebar entry points. */
  landingPath: string
  showInSidebar: boolean
  tabs: HeaderTab[]
  reportTabs: HeaderTab[]
  canOpen: (pathname: string) => boolean
}

/** Who may open which part of the Allocation module: Hakio staff all of it, admins
 * of an allowed organization (`~/utils/allocationAccess`) only the Products report. */
export function useAllocationAccess(): AllocationAccess {
  const { permissions } = useUser()
  const { user } = useAuth0()
  const [showInternalFeatures] = useShowInternalFeatures()
  const productReport = canOpenProductReport(user?.org_name, permissions.canAdminister)
  return useMemo((): AllocationAccess => {
    if (permissions.isStaff) {
      return {
        level: "full",
        landingPath: "/allocation",
        showInSidebar: showInternalFeatures,
        tabs: MODULE_TABS,
        reportTabs: REPORT_TABS,
        canOpen: () => true,
      }
    }
    return {
      level: productReport ? "productReport" : "none",
      landingPath: PRODUCT_REPORT_PATH,
      showInSidebar: productReport,
      tabs: PRODUCT_REPORT_MODULE_TABS,
      reportTabs: PRODUCT_REPORT_TABS,
      canOpen: (pathname) => productReport && pathname === PRODUCT_REPORT_PATH,
    }
  }, [permissions.isStaff, productReport, showInternalFeatures])
}
